"use client";

import { DefaultError, QueryFunctionContext, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useCallback } from "react";
import useUserApi from "src/lib/filmwebid/useUserApi";
import { MovieInfoQuery } from "src/lib/movieinfo/movieinfotypes";

export type UserApiQueryOptions = {
    method?: string;
    allowAnon: boolean;
}

export type UserApiGraphqlResponse = {
    data: MovieInfoQuery
}
type UserApiGraphqlQueryKey = readonly [string, string, { query: string, variables?: any }?];

const defaultQueryOptions = {
    refetchOnWindowFocus: false,
    staleTime: 0,
}

export type UserApiQueryKey = readonly [string, string, any?];

export function useUserApiQuery<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData>(path: string, params?: any, userApiOptions?: UserApiQueryOptions, options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, UserApiQueryKey>, "queryKey" | "queryFn">) {
    const queryFn = useUserApiQueryFunction(userApiOptions?.method ?? "GET", userApiOptions?.allowAnon ?? true, false);
    const queryOptions: UseQueryOptions<TQueryFnData, TError, TData, UserApiQueryKey> = {
        ...defaultQueryOptions,
        ...options,
        queryKey: buildUserApiQueryKey(path, params),
        queryFn,
    };
    return useQuery(queryOptions);
}
export function useUserApiGraphqlQuery<TData = UserApiGraphqlResponse, TError = DefaultError, TQueryFnData = UserApiGraphqlResponse>(query: string, variables?: any, userApiOptions?: UserApiQueryOptions, options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, UserApiQueryKey>, "queryKey" | "queryFn">) {
    const queryFn = useUserApiQueryFunction(userApiOptions?.method ?? "GET", userApiOptions?.allowAnon ?? true, true);
    const queryOptions: UseQueryOptions<TQueryFnData, TError, TData, UserApiQueryKey> = {
        ...defaultQueryOptions,
        ...options,
        queryKey: buildUserApiGraphqlQueryKey(query, variables),
        queryFn,
    };
    return useQuery(queryOptions);

}
export function buildUserApiQueryKey(path: string, params?: any): UserApiQueryKey {
    return ["userapi", path, params];
}
export function buildUserApiGraphqlQueryKey(query: string, variables?: any): UserApiGraphqlQueryKey {
    let keyObj: UserApiGraphqlQueryKey[2] = { query };
    if (variables) {
        keyObj.variables = variables;
    }
    return buildUserApiQueryKey("graphql", keyObj);
}
export function useUserApiQueryFunction(method: string, allowAnon: boolean, withFilminfo: boolean) {
    const fetcher = useUserApi(withFilminfo);
    return useCallback(async (context: QueryFunctionContext<UserApiQueryKey>): Promise<any> => {
        const [_key, path, params] = context.queryKey;
        if (path === "graphql") {
            const { query, variables } = params;
            return fetcher("", {
                method,
                allowAnon,
                params: { query, variables: serializeVariables(variables) },
                signal: context.signal
            });
        }

        return fetcher(path, {
            method,
            allowAnon,
            params: context.pageParam ? { ...context.pageParam, ...params } : params,
            signal: context.signal
        });
    }, [fetcher, method, allowAnon]);
}
function serializeVariables(variables: any): any {
    if (variables) {
        return JSON.stringify(variables);
    }
    return undefined;
}